import { FormEventHandler, memo, useCallback, useMemo, useState } from 'react'
import LogoImgLight from 'assets/images/svg/crewworks-slim-light.svg'
import LogoImgDark from 'assets/images/svg/crewworks-slim-dark.svg'
import { useAppAccountRegistration } from './useAppAccountRegistration'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewTextBoxField } from 'components/forms/crewTextBoxField'
import classNames from 'classnames'
import { CrewReCaptcha } from 'components/elements/crewReCaptcha/crewReCaptcha'
import { useTranslation } from '@crew/modules/i18n'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useAppSelector } from 'states/hooks'
import { useReCaptcha } from 'hooks/useReCaptcha'
import { FormValues } from './useAppAccountRegistration'
import { CrewCheckBoxField } from 'components/forms/crewCheckBoxField'
import { CrewTermsOfServiceAndPrivacyPolicyLink } from 'components/elements/crewTermsOfServiceAndPrivacyPolicyLink/crewTermsOfServiceAndPrivacyPolicyLink'
import { isApiErrorResult } from '@crew/apis/errors'
import { BillingCycle, ContractPlan } from '@crew/enums/app'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import { FAQ_URL } from '@crew/configs/constants'
import { useMappedSearchParams } from 'hooks/useMappedSearchParams'

export const AppAccountRegistration = memo(() => {
  const {
    control,
    handleSubmit,
    formState,
    validateRules,
    registerAccount,
    watch,
    isRequestAccountRegistrationLoading,
  } = useAppAccountRegistration()

  const { t } = useTranslation()
  const { navigate } = useCrewNavigate()
  const themeMode = useAppSelector((state) => state.app.currentTheme)

  const [serverErrorMessage, setServerErrorMessage] = useState('')

  // planとbillingCycleを単体の値として取得し、その他をrestParamsとして取得する
  const { ...restParams } = useMappedSearchParams()

  const { isReCaptchaVerified } = useReCaptcha()

  const agree = watch('agree')

  const canSend = useMemo(
    // formState.isValidはerrorsが空でもfalseになることがあるためerrorsで判定する
    () =>
      formState.isDirty &&
      Object.keys(formState.errors).length === 0 &&
      isReCaptchaVerified &&
      !isRequestAccountRegistrationLoading,
    // formStateはproxyなのでformState自体をlistenする必要がある
    // https://react-hook-form.com/api/useform/formstate
    [formState, isReCaptchaVerified, isRequestAccountRegistrationLoading]
  )

  // 「無料で試してみる」ボタンクリック
  const handleSubmitButtonClick = useCallback(() => {
    const onSubmit = async (values: FormValues) => {
      try {
        // Execute register account process
        await registerAccount(
          values,
          ContractPlan.Free,
          BillingCycle.Year,
          restParams
        )

        navigate('/verify_email')
      } catch (error) {
        if (window.grecaptcha) {
          window.grecaptcha.reset()
        }

        // ApiErrorの場合はcodeから文字列リソースを取得する
        const decodedError = isApiErrorResult(error)
        if (decodedError) {
          setServerErrorMessage(
            t(
              `message.apiError.${error.data.code}`,
              error.data as { [key: string]: any }
            )
          )
        } else {
          // ApiErrorでない場合はエラー自体が不明エラーを表示する
          setServerErrorMessage(t('message.apiError.commonHttpError'))
        }
      }
    }
    handleSubmit(onSubmit)()
  }, [handleSubmit, t, registerAccount, restParams, navigate])

  // clear message error by server when change text value
  const handleEmailTextBoxValueChanged = useCallback(() => {
    setServerErrorMessage('')
  }, [])

  const LogoImg = useMemo(
    () => (themeMode === 'dark' ? LogoImgDark : LogoImgLight),
    [themeMode]
  )

  const errorMessageBaseClassNames = useMemo(() => {
    return 'crew-error-text text-start text-sm'
  }, [])

  // Form内Enterキー押下によるSubmit制御
  // TODO: 一時的な暫定対応。対処方法についてはCREW-3338で検討する。
  //       https://break-tmc.atlassian.net/browse/CREW-3338
  const handleFormSubmit = useCallback<FormEventHandler>((event) => {
    event.preventDefault()
  }, [])

  return (
    <div className="flex flex-row h-[100dvh] pt-4 sm:pt-12 justify-center crew-bg-gray-1">
      <div className="flex flex-col items-center">
        <div className="flex flex-col gap-4 sm:gap-6 w-full max-w-sm">
          <div className="flex flex-row items-center sm:mb-6">
            {/* ロゴ */}
            <img src={LogoImg} alt="logo" className="mx-auto h-16 w-auto" />
          </div>
          <form
            className="flex flex-col gap-1"
            // Form内Enterキー押下によるSubmit制御
            // TODO: 一時的な暫定対応。対処方法についてはCREW-3338で検討する。
            //       https://break-tmc.atlassian.net/browse/CREW-3338
            onSubmit={handleFormSubmit}
          >
            <div className="flex flex-col gap-1">
              <div>
                <CrewTextBoxField
                  id="email"
                  name="email"
                  control={control}
                  placeholder={t('label.mailAddress')}
                  labelMode="floating"
                  rules={validateRules.email}
                  inputAttr={{
                    'data-testid': 'username',
                  }}
                  showLabel={false}
                  onValueChanged={handleEmailTextBoxValueChanged}
                />
              </div>
              {/* サーバーエラーメッセージ */}
              <div
                className={classNames(errorMessageBaseClassNames, {
                  'h-4': serverErrorMessage.length > 0,
                })}
                data-testid="login-form-server-error-message"
              >
                {serverErrorMessage}
              </div>
            </div>
            {/* 「利用規約とプライバシーポリシーに同意します」 */}
            <div className="flex flex-row justify-center gap-3 py-2.5">
              <CrewCheckBoxField
                id="agree"
                name="agree"
                control={control}
                rules={validateRules.agree}
              />
              <CrewTermsOfServiceAndPrivacyPolicyLink />
            </div>
            {/* SignUp button */}
            <div className="flex flex-row justify-center">
              <CrewButton
                className="grow"
                type="primary"
                disabled={!canSend || !agree}
                data-testid="signup"
                text={t('action.tryFreePlan')}
                onClick={handleSubmitButtonClick}
              />
            </div>

            {/* Recaptcha */}
            <CrewReCaptcha />
          </form>
        </div>
        {/* キャンペーン用テキスト*/}
        <div className="flex flex-col gap-2.5 max-w-xl">
          <div className="flex flex-col items-center mt-10 mb-3 text-3xl font-bold text-crew-blue-500 dark:text-crew-blue-600">
            <div>{t('message.startCampaignNotes.availableFeatures.line1')}</div>
            <div>{t('message.startCampaignNotes.availableFeatures.line2')}</div>
          </div>
          <div className="leading-tight whitespace-pre-wrap">
            {t('message.startCampaignNotes.guidance.afterCampaignEnds')}
          </div>
          <div>
            {t('message.startCampaignNotes.guidance.needHelp.prefix')}
            <CrewLink to={FAQ_URL} target="_blank">
              {t('message.startCampaignNotes.guidance.needHelp.link')}
            </CrewLink>
            {t('message.startCampaignNotes.guidance.needHelp.suffix')}
          </div>
        </div>
      </div>
    </div>
  )
})
